import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect for header background
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/matches', label: 'Matches' },
    { path: '/teams', label: 'Teams & Groups' },
    { path: '/highlights', label: 'Highlights' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-gray-950/90 backdrop-blur-lg shadow-lg border-b border-gray-800' 
          : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
          >
            <div className="bg-emerald-600/30 p-2.5 rounded-xl flex items-center justify-center">
              <img
                src="/Hirez.png"
                alt="AFCON 2025 Logo"
                className="w-9 h-9 object-contain scale-110"
              />
            </div>
            <span className="text-xl sm:text-2xl font-bold text-white tracking-tight">
              AFCON <span className="text-emerald-400">2025</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-1 lg:space-x-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`
                  relative px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-all duration-200
                  ${isActive(link.path)
                    ? 'text-white bg-emerald-600/30'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800/50'
                  }
                `}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                )}
              </Link>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800/50 transition"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <div
        className={`
          md:hidden overflow-hidden transition-all duration-300 ease-in-out
          ${isOpen ? 'max-h-96 py-4' : 'max-h-0 py-0'}
          bg-gray-950/95 backdrop-blur-lg border-b border-gray-800
        `}
      >
        <div className="container mx-auto px-4 flex flex-col space-y-2">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`
                flex items-center justify-between px-5 py-3 rounded-lg text-base font-medium
                transition-all duration-200
                ${isActive(link.path)
                  ? 'bg-emerald-600/30 text-white'
                  : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'
                }
              `}
              onClick={() => setIsOpen(false)}
            >
              <span>{link.label}</span>
              {isActive(link.path) && <ChevronDown className="rotate-180" size={20} />}
            </Link>
          ))}
        </div>
      </div>
    </header>
  );
};